import {useQueries, useQueryClient, type UseQueryOptions, type UseQueryResult} from '@tanstack/react-query'
import {useMemo, useRef} from 'react'

import {
  getCachedSubIssuesQueryDataFromMap,
  readSubIssuesCacheMap,
} from '../../../features/sub-issues/sub-issues-session-cache'
import {useShowToastOnError} from '../../../hooks/use-show-toast-on-error'
import {useViews} from '../../../hooks/use-views'
import {ApiError} from '../../../platform/api-error'
import {buildColumnIdsForItemsData} from '../../columns/columns-state-provider'
import {buildGroupValuesFromQueryData} from '../query-client-api/memex-groups'
import {getQueryDataForSubIssuesPage} from '../query-client-api/memex-items/get-query-data'
import {nextPlaceholderPageInfo} from '../query-client-api/page-params'
import {GroupedItemBatchRequestResolver, paginatedViewRequestResolverFactory} from './paginated-view-request-resolver'
import {
  isPageParamsDataGrouped,
  isPageParamsDataGroupedWithSecondaryGroups,
  isPageParamsDataWithSubIssues,
} from './query-data-helpers'
import type {
  GroupedItemBatchQueryKey,
  MemexSubIssuesQueryKey,
  PageType,
  PaginatedMemexItemsQueryKey,
} from './query-keys'
import {
  buildGroupedItemBatchQueryKey,
  buildMemexItemsOrGroupsQueryKey,
  createGroupedItemsPageTypeFromGroupedItemsId,
  createSubIssuesPageTypeFromParentKey,
  isPageOfItems,
  isPageTypeForSubIssues,
  pageTypeForGroups,
  pageTypeForSecondaryGroups,
  pageTypeForUngroupedItems,
} from './query-keys'
import {
  type GroupedItemBatchPageQueryData,
  type MemexPageQueryData,
  type PageParam,
  pageParamForNextPlaceholder,
  type PageParamsQueryData,
  type PaginatedMemexItemsQueryVariables,
} from './types'
import {useMemexItemsPageParams} from './use-memex-items-page-params'
import {useNextPlaceholderQuery} from './use-next-placeholder-query'

type UsePaginatedMemexItemsActiveQueriesReturnType = {
  queryKeys: Array<PaginatedMemexItemsQueryKey>
  queries: Array<UseQueryResult<MemexPageQueryData>>
  groupedItemBatchesQueryKeys: Array<GroupedItemBatchQueryKey>
  groupedItemBatchesQueries: Array<UseQueryResult<GroupedItemBatchPageQueryData>>
}

/**
 * Based on data stored in the `pageParamsQueryData` query, this hook
 * builds up a list of options to pass to `useQueries`, so that we can dynamically
 * monitor multiple queries representing memex items data.
 * @param variables PaginatedMemexItemsQueryVariables to use in QueryKey
 * @returns UsePaginatedMemexItemsActiveQueriesReturnType
 */
export function usePaginatedMemexItemsActiveQueries(
  variables: PaginatedMemexItemsQueryVariables,
): UsePaginatedMemexItemsActiveQueriesReturnType {
  const {pageParamsQueryData} = useMemexItemsPageParams(variables)
  const groupedItemBatchesResult = useGroupedItemBatchesQueries(variables, pageParamsQueryData)
  return {
    ...useItemsAndGroupsQueries(variables, pageParamsQueryData),
    ...groupedItemBatchesResult,
  }
}

function useItemsAndGroupsQueries(
  variables: PaginatedMemexItemsQueryVariables,
  pageParamsQueryData: PageParamsQueryData,
) {
  const queryClient = useQueryClient()
  const {updateMemexItemsNextPlaceholderQuery, updateMemexGroupsNextPlaceholderQuery} =
    useNextPlaceholderQuery(variables)
  const {addLoadedFieldIdForCurrentView} = useViews()
  const showToastOnError = useShowToastOnError()

  // Build up a set of options to pass to useQueries
  const queries = useMemo(() => {
    const options: Array<UseQueryOptions<MemexPageQueryData>> = []
    // This will be a flat list of query keys (in this case just the groupId + pageIndex pairs) for
    // all queries/pages that we want to monitor.
    const queryKeys = buildQueryKeysFromPageParamsQueryData(pageParamsQueryData)
    const groupValuesMap = buildGroupValuesFromQueryData(queryClient)
    const subIssuesCacheMap = readSubIssuesCacheMap()
    for (const {pageType, pageParam} of queryKeys) {
      const queryKey = buildMemexItemsOrGroupsQueryKey(variables, pageType, pageParam)

      // Only hydrate from sessionStorage when TanStack's cache is empty for this key.
      const cachedInitialData =
        isPageTypeForSubIssues(pageType) &&
        getQueryDataForSubIssuesPage(queryClient, queryKey as MemexSubIssuesQueryKey) == null
          ? getCachedSubIssuesQueryDataFromMap(subIssuesCacheMap, pageType.nwoReference)
          : undefined

      options.push({
        queryKey,
        // initialDataUpdatedAt: 0 marks cached data as stale so React Query refetches immediately.
        ...(cachedInitialData != null ? {initialData: cachedInitialData, initialDataUpdatedAt: 0} : {}),
        queryFn: async ({signal}) => {
          if (pageParam === pageParamForNextPlaceholder) {
            // We never want to be making a request for this query, as it is just a placeholder
            if (pageType === pageTypeForGroups || pageType === pageTypeForSecondaryGroups) {
              return {groups: [], pageInfo: nextPlaceholderPageInfo}
            }
            return {nodes: [], pageInfo: nextPlaceholderPageInfo}
          }

          const requestResolver = paginatedViewRequestResolverFactory(variables, groupValuesMap, pageType, pageParam)
          try {
            const response = await requestResolver.fetchData(signal)
            // if we abort, then there is nothing to do with what was fetched, so return dummy content to appease
            // typescript (see https://tanstack.com/query/latest/docs/framework/react/guides/query-cancellation)
            if (signal.aborted) {
              return {nodes: [], pageInfo: nextPlaceholderPageInfo, totalCount: {value: 0, isApproximate: false}}
            }

            requestResolver.handleResponse(queryClient)

            const {totalCount, ...responseWithoutTotalCount} = response

            if ('groups' in responseWithoutTotalCount) {
              // Handle next_placeholder data for top-level groups, if any exist
              updateMemexGroupsNextPlaceholderQuery(responseWithoutTotalCount)
            } else if (!isPageTypeForSubIssues(pageType) && isPageOfItems(pageType)) {
              // Handle next_placeholder data for items, if any exist
              // Exclude sub-issues since we do not paginate them
              updateMemexItemsNextPlaceholderQuery(responseWithoutTotalCount, pageType)
            }

            // The server supplements requested fieldIds with additional
            // required fields based on the view type.
            const receivedFieldIds = buildColumnIdsForItemsData(response)
            // Mark received fields as loaded
            for (const id of receivedFieldIds || []) {
              addLoadedFieldIdForCurrentView(id)
            }
          } catch (error) {
            // For now, we just want to show the toast when we've received a retryable error.
            if (error instanceof ApiError && error.status === 503) {
              showToastOnError(error)
            } else {
              throw error
            }
          }
          return requestResolver.queryData()
        },
        structuralSharing: false,
        // staleTime: 0 for cached sub-issues triggers immediate background refetch.
        staleTime: cachedInitialData != null ? 0 : Infinity,
      })
    }
    return options
  }, [
    pageParamsQueryData,
    queryClient,
    variables,
    updateMemexGroupsNextPlaceholderQuery,
    updateMemexItemsNextPlaceholderQuery,
    addLoadedFieldIdForCurrentView,
    showToastOnError,
  ])

  const queryKeys = useMemo(() => queries.map(q => (q.queryKey as PaginatedMemexItemsQueryKey) || []), [queries])
  const queryResults = useQueries({
    queries,
  })
  const memoizedQueries = useQueriesMemo(queryResults)

  // The queries that are returned by `useQueries` do not have any information about their own query keys,
  // so we return the queryKeys alongside them so that downstream consumers can map back and forth by index
  return {
    queries: memoizedQueries,
    queryKeys,
  }
}

function useGroupedItemBatchesQueries(
  variables: PaginatedMemexItemsQueryVariables,
  pageParamsQueryData: PageParamsQueryData,
) {
  const {updateMemexGroupsNextPlaceholderQuery} = useNextPlaceholderQuery(variables)
  const queryClient = useQueryClient()

  const queries = useMemo(() => {
    const options: Array<UseQueryOptions<GroupedItemBatchPageQueryData>> = []

    if (isPageParamsDataGroupedWithSecondaryGroups(pageParamsQueryData)) {
      for (const groupedItemsBatch of pageParamsQueryData.groupedItemBatches) {
        options.push({
          // eslint-disable-next-line @tanstack/query/exhaustive-deps
          queryKey: buildGroupedItemBatchQueryKey(variables, groupedItemsBatch),
          queryFn: async ({signal}) => {
            const requestResolver = new GroupedItemBatchRequestResolver(variables, groupedItemsBatch)
            const response = await requestResolver.fetchData(signal)
            updateMemexGroupsNextPlaceholderQuery(response)
            requestResolver.handleResponse(queryClient)

            // We don't actually care to store anything in this query - we're only using it
            // for the side-effects; however, `react-query` will complain if we have a
            // query that doesn't return anything.
            return groupedItemsBatch
          },
          structuralSharing: false,
          staleTime: Infinity,
        })
      }
    }

    return options
  }, [pageParamsQueryData, queryClient, updateMemexGroupsNextPlaceholderQuery, variables])

  const groupedItemBatchesQueryKeys = useMemo(() => queries.map(q => q.queryKey as GroupedItemBatchQueryKey), [queries])
  const queryResults = useQueries({
    queries,
  })
  const groupedItemBatchesQueries = useQueriesMemo(queryResults)

  // The queries that are returned by `useQueries` do not have any information about their own query keys,
  // so we return the queryKeys alongside them so that downstream consumers can map back and forth by index
  return {
    groupedItemBatchesQueries,
    groupedItemBatchesQueryKeys,
  }
}

/**
 * Builds a set of query keys based on a PageParamsQueryData object.
 * For ungrouped data, we return an array of {pageType: pageTypeForUngroupedItems, pageParam: pageParam} for each entry
 * in our pageParamsQueryData.pageParams.
 * .
 * For grouped data, we return an array of {pageType: pageTypeForGroups, pageParam: pageParam} for each entry, and
 * we also return keys for each individual group's pageParams.
 * We do this by building a map of groupId -> pageParams, and then returning
 * {pageType: PageTypeForGroupedItems, pageParam: pageParam} for each pageParam of each group.
 *
 * In either case, we additionally check for a `parentIssues` field on the pageParamsQueryData,
 * and if it exists, return an array of {pageType: {parentIssueId}, pageParam: pageParam} for each entry.
 */
function buildQueryKeysFromPageParamsQueryData(
  pageParamsQueryData: PageParamsQueryData,
): Array<{pageType: PageType; pageParam: PageParam}> {
  // Check for sub-issues for both grouped and ungrouped data
  const queryKeysForSubIssues = isPageParamsDataWithSubIssues(pageParamsQueryData)
    ? Object.entries(pageParamsQueryData.parentIssues).flatMap(([parentIssueKey, pageParams]) => {
        const pageType = createSubIssuesPageTypeFromParentKey(parentIssueKey)
        return pageParams.map(pageParam => ({
          pageType,
          pageParam,
        }))
      })
    : []

  if (isPageParamsDataGrouped(pageParamsQueryData)) {
    const queryKeysForItems = Object.entries(pageParamsQueryData.groupedItems).flatMap(([groupId, pageParams]) =>
      pageParams.map(pageParam => ({pageType: createGroupedItemsPageTypeFromGroupedItemsId(groupId), pageParam})),
    )
    const queryKeysForGroups = pageParamsQueryData.pageParams.map(
      pageParam =>
        ({
          pageType: pageTypeForGroups,
          pageParam,
        }) as const,
    )

    const queryKeysForSecondaryGroups = isPageParamsDataGroupedWithSecondaryGroups(pageParamsQueryData)
      ? pageParamsQueryData.secondaryGroups.map(
          pageParam =>
            ({
              pageType: pageTypeForSecondaryGroups,
              pageParam,
            }) as const,
        )
      : []

    return [...queryKeysForItems, ...queryKeysForGroups, ...queryKeysForSecondaryGroups, ...queryKeysForSubIssues]
  } else {
    const queryKeysForItems = pageParamsQueryData.pageParams.map(
      pageParam =>
        ({
          pageType: pageTypeForUngroupedItems,
          pageParam,
        }) as const,
    )

    return [...queryKeysForItems, ...queryKeysForSubIssues]
  }
}

/**
 * `useQueries` returns an array of `UseQueryResult` objects. The array returned here
 * will be a new object each time, which makes it impossible the use the return value
 * in the dependencies of any downstream `useMemo`s. This function works around
 * this behavior by using a custom algorithm for determining whether or not to return a new array
 * based on `data` attributes of each item in the array, which is what we want to use
 * to indicate we need to re-memoize because something about the items has changed.
 */
function useQueriesMemo<T>(queriesResultArray: Array<UseQueryResult<T>>) {
  // this holds reference to previous value - it will only change if
  // the number of queries in the array changes or the data of a query changes
  const stableQueriesArrayRef = useRef<Array<UseQueryResult<T>>>([])
  // check if the queries length is the same and the data in each query is the same
  const areArraysConsideredTheSame =
    // eslint-disable-next-line react-hooks/refs
    stableQueriesArrayRef.current && queriesResultArray.length === stableQueriesArrayRef.current.length
      ? // eslint-disable-next-line react-hooks/refs
        queriesResultArray.every((element, i) => {
          const dataAtIndexIsTheSame =
            stableQueriesArrayRef.current && element.data === stableQueriesArrayRef.current[i]?.data

          if (dataAtIndexIsTheSame) {
            // Even though the data is the same, we could have a new query result here, so let's update the reference
            // of the stableQueriesArrayRef for this query result.

            // If the data is not the same, then we will replace the entire stableQueriesArrayRef below.
            stableQueriesArrayRef.current[i] = element
          }

          return dataAtIndexIsTheSame
        })
      : false

  //only update the ref tracking the queries array if they are different
  if (!areArraysConsideredTheSame) {
    // eslint-disable-next-line react-hooks/refs
    stableQueriesArrayRef.current = queriesResultArray
  }

  // eslint-disable-next-line react-hooks/refs
  return stableQueriesArrayRef.current
}
